import { SearchIcon, XIcon } from 'lucide-react';
import type * as React from 'react';

type DimeFilterListSearchProps = Readonly<{
  inputRef: React.Ref<HTMLInputElement>;
  /** The list's own name, which the box reads as "Search <label>". */
  label: string;
  listId: string;
  activeRowId?: string;
  value: string;
  onValueChange: (text: string) => void;
  placeholder: string;
  maxLength?: number;
  /** A control at the row's end, after the X, such as a filter's Select All. */
  action?: React.ReactNode;
}>;

/** A filter list's search box (icon, plain input, full-width rule, and an X while it holds text), a combobox over the list below it. */
export function DimeFilterListSearch({
  inputRef,
  label,
  listId,
  activeRowId,
  value,
  onValueChange,
  placeholder,
  maxLength,
  action,
}: DimeFilterListSearchProps) {
  return (
    <div data-slot="filter-list-search" className="flex h-9 items-center gap-2 border-b px-3">
      <SearchIcon aria-hidden className="size-4 shrink-0 opacity-50" />
      <input
        ref={inputRef}
        type="text"
        role="combobox"
        aria-label={`Search ${label}`}
        aria-expanded
        aria-controls={listId}
        aria-activedescendant={activeRowId}
        aria-autocomplete="list"
        autoComplete="off"
        autoCorrect="off"
        spellCheck={false}
        className="flex h-10 w-full rounded-md bg-transparent py-3 text-sm outline-hidden placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50"
        placeholder={placeholder}
        value={value}
        maxLength={maxLength}
        onChange={(event) => onValueChange(event.target.value)}
      />
      {value ? (
        <button
          type="button"
          aria-label="Clear search"
          className="flex size-6 shrink-0 items-center justify-center rounded-sm text-muted-foreground hover:bg-muted hover:text-foreground"
          onClick={(event) => {
            onValueChange('');
            // The button goes once the search is empty, so focus returns to the input rather than the page.
            event.currentTarget.parentElement?.querySelector('input')?.focus();
          }}
        >
          <XIcon aria-hidden className="size-3.5" />
        </button>
      ) : null}
      {action}
    </div>
  );
}
